import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  withWidth,
  SwipeableDrawer,
  CircularProgress,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/itemDetailsDrawerStyle.js';
import cx from 'classnames';
import RegularButton from '../../../components/CustomButtons/Button';
import { SmallUppercase, SecondaryH5, H4 } from 'text';
import SegmentedControl from 'components/SegmentedControl';
import margin from 'assets/scss/custom/margin.module.scss';
import Variants from './Variants';
import Pricing from './Pricing';
import Ordinal from './Ordinal';
import Deals from './Deals';
import TakeAction from './TakeAction';
import Collapsible from '../Collapsible';
import {useSelector} from "react-redux";
import { getVendorProduct, updateVendorProduct } from '../../../api/index';

const useStyles = makeStyles(styles);
function ItemDetails({ skuId, idx, close, width, productQuantity, updateLocal, canSetStock, canEditPrice, canEditDeals, user }) {
  const [variants, setVariants] = useState([]);
  const [price, setPrice] = useState();
  const [sku, setSku] = useState('');
  const [product, setProduct] = useState({});
  const [quantity, setQuantity] = useState(productQuantity);
  const [packageQty, setPackageQty] = useState();
  const [packageSaveHowMuch, setPackageSaveHowMuch] = useState();
  const [retailerDisabled, setRetailerDisabled] = useState(false);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const activeStore = useSelector(state => state.app.activeStore);
  const [ordinal, setOrdinal] = useState();

  const title = product.title;
  const subtitle = product.subtitle;
  const abv = product && product.abv ? product.abv : null;

  useEffect(() => {
    setQuantity(productQuantity);
  }, [productQuantity]);

  useEffect(() => {
    loadProductData(skuId);
  }, [skuId]);

  const loadProductData = skuId => {
    if (skuId) {
      setLoading(true);
      getVendorProduct(skuId)
        .then(res => {
          let product = res.data[0];
          if (product) {
            setSku(skuId);
            setProduct(product);
            setQuantity(product.quantity);
            setPrice(product.source_price);
            setOrdinal(product.ordinal);
            setVariants(product.volume_variants);
            setPackageQty(product.package_qty);
            setPackageSaveHowMuch(product.package_item_price && product.package_qty ? parseFloat((product.regular_price - product.package_item_price)* product.package_qty).toFixed(2): null);
          }
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setLoading(false);
        });
    }
  };

  const handleUpdateProduct = () => {
    // should be sent as numbers, inputs originally strings
    let toNumberpackageSaveHowMuch = parseFloat(packageSaveHowMuch, 10);
    let toNumberpackageQty = parseInt(packageQty, 10);
    let payload = {
      quantity,
      price,
      ordinal,
      package_qty: toNumberpackageQty ? toNumberpackageQty : 0,
      package_save_how_much: toNumberpackageSaveHowMuch ? toNumberpackageSaveHowMuch : 0,
      retailer_disabled: retailerDisabled
    };
    updateVendorProduct(sku)(payload)
      .then(res => {
        // refresh Algolia results after updateVendor - potential race case as Algola results can still be updating after updateVendorProduct returns response.
        setLoading(true);
        updateLocal(idx, `${activeStore}_buy_price`, parseFloat(payload.price));
        updateLocal(idx, `${activeStore}_qty`, payload.quantity);
        updateLocal(idx, `${activeStore}_package_qty`, payload.package_qty);
        setLoading(false)
        close();
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
        close();
      });
  };

  const renderContent = () => {
    let content = null;
    let isAdmin = user && user.type === "saucey_admin";
    if (loading) {
      content = <CircularProgress className={classes.spinner} />;
    } else {
      content = (
        <>
          <div className={classes.details}>
            <img src={product.image} />
          </div>
          <SegmentedControl
            isInStock={quantity}
            className={margin.top_md}
            setQuantity={canSetStock ? setQuantity  : () => {}}
            canEdit={canSetStock}
          />
          <div className={margin.top_sm}>
            <SecondaryH5>{title}</SecondaryH5>
            <H4>{subtitle}</H4>
            <div className={cx(classes.flex, margin.top_xs)}>
              <SecondaryH5 uppercase>abv&nbsp;</SecondaryH5>
              <H4>{abv}%</H4>
            </div>
          </div>
          <Variants
            variants={variants}
            sku={sku}
            loadProductData={loadProductData}
          />
          <Collapsible listTitle="Pricing">
          <Pricing price={price} setPrice={canEditPrice? setPrice : () => {}} canEdit={canEditPrice}/>
              </Collapsible>
          {isAdmin && 
            <Collapsible listTitle="Global Ordinal">
              <Ordinal ordinal={ordinal} setOrdinal={setOrdinal}/>
            </Collapsible>
          }
          <Divider light />
          <Deals
            styles={styles}
            packageQty={packageQty}
            packageSaveHowMuch={packageSaveHowMuch}
            setPackageQty={canEditDeals ? setPackageQty : () => {}}
            setPackageSaveHowMuch={canEditDeals ? setPackageSaveHowMuch : () => {}}
            canEdit={canEditDeals}
          />
          <Divider light />
          <TakeAction sku={sku} setRetailerDisabled={setRetailerDisabled} />
          <Divider light />
          <div className={classes.saveButtonContainer}>
            <RegularButton
              className={classes.saveButton}
              color="info"
              fullWidth
              size="sm"
              onClick={() => handleUpdateProduct()}
            >
              <SmallUppercase light>save changes</SmallUppercase>
            </RegularButton>
          </div>
        </>
      );
    }
    return content;
  };

  return (
    <SwipeableDrawer
      anchor={width === 'xs' ? 'bottom' : 'right'}
      onOpen={() => {}}
      onClose={close}
      open={!!skuId}
    >
      <div
        className={cx(
          { [classes.container]: true },`
          { [classes.loading]: loading }`
        )}
      >
        {renderContent()}
      </div>
    </SwipeableDrawer>
  );
}

export const FlexRow = ({ children, ...rest }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}
    {...rest}
  >
    {children}
  </div>
);

ItemDetails.propTypes = {
  skuId: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired
};

export default withWidth()(ItemDetails);
